import { hasError } from '@repo/shared/api/helpers'
import { Button, ButtonVariant } from '@repo/ui/button'
import { useAsyncData } from '@repo/ui/dataProvider'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@repo/ui/dialog'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@repo/ui/form'
import { useFormValidator, useT } from '@repo/ui/hooks'
import { Input } from '@repo/ui/input'
import { Textarea } from '@repo/ui/textarea'
import { cn } from '@repo/ui/utils'
import { PenLineIcon, UploadCloudIcon, UserPlus } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'

import { Uploader } from '@/components/beneficiary/FileUploader.tsx'
import { useView } from '@/hooks'
import { api } from '@/main.tsx'
import { AvailableParams } from '@/routes/router'
import { storeActions } from '@/store/actions'
import { getCurrentOrganization } from '@/store/helpers'
import { useStoreDispatch } from '@/store/hooks'
import { beneficiarySchema } from '@/validation-schemas'

const keyPrefix = 'beneficiary.creator'
export function BeneficiaryCreatorTrigger({
  className,
  variant,
}: {
  className?: string
  variant?: ButtonVariant
}) {
  const { t } = useT({ keyPrefix })
  const [open, setOpen] = useState<boolean>(false)
  return (
    <>
      <Button variant={variant} className={cn('w-full', className)} onClick={() => setOpen(true)}>
        <UserPlus className="size-4" />
        {t('createBeneficiary')}
      </Button>
      {open && <BeneficiaryCreator modalHandler={{ open, setOpen }} />}
    </>
  )
}

export function BeneficiaryCreator({
  modalHandler,
}: {
  modalHandler: { open: boolean; setOpen: (v: boolean) => void }
}) {
  const [creationMode, setCreationMode] = useState<'manual' | 'upload' | null>(null)
  return (
    <Dialog open={modalHandler.open} onOpenChange={modalHandler.setOpen}>
      <DialogContent
        size={creationMode ? 'wide' : 'default'}
        className="max-h-[75vh]"
        disableOutsideClick
      >
        {creationMode === null ? (
          <Step1 onSelectMode={(mode) => setCreationMode(mode)} />
        ) : (
          <Step2 mode={creationMode} onCreate={() => modalHandler.setOpen(false)} />
        )}
      </DialogContent>
    </Dialog>
  )
}

function Step1({ onSelectMode }: { onSelectMode: (mode: 'manual' | 'upload') => void }) {
  const { t } = useT({ keyPrefix })
  return (
    <>
      <DialogHeader>
        <DialogTitle>{t('selectMode.title')}</DialogTitle>
        <DialogDescription>{t('selectMode.description')}</DialogDescription>
      </DialogHeader>

      <DialogFooter>
        <div className="justify-between flex w-full">
          <Button variant="outline" onClick={() => onSelectMode('manual')}>
            <PenLineIcon className="size-4" /> {t('selectMode.modes.manual')}
          </Button>
          <Button variant="outline" onClick={() => onSelectMode('upload')}>
            <UploadCloudIcon className="size-4" /> {t('selectMode.modes.upload')}
          </Button>
        </div>
      </DialogFooter>
    </>
  )
}

function Step2({ mode, onCreate }: { mode: 'manual' | 'upload'; onCreate: () => void }) {
  const { t } = useT({ keyPrefix })

  return (
    <>
      <DialogHeader>
        <DialogTitle>{t('title')}</DialogTitle>
        <DialogDescription>{t('description')}</DialogDescription>
      </DialogHeader>

      {mode === 'manual' ? (
        <BeneficiaryForm onCreate={onCreate} />
      ) : (
        <SmartCreator onCreate={onCreate} />
      )}
    </>
  )
}

function SmartCreator({ onCreate }: { onCreate: () => void }) {
  const dispatch = useStoreDispatch()
  const [file, setFile] = useState<File>()
  const [isProcessing, setIsProcessing] = useState<boolean>(false)
  const [values, setValues] = useState<{
    fullName: string
    profile: { value: string }
  }>({ fullName: '', profile: { value: '' } })
  const handleSubmit = (beneficiaryId: string) => {
    if (file) {
      void dispatch(
        storeActions.beneficiary.document.create({
          beneficiaryId,
          file,
        }),
      )
    }
    onCreate()
  }
  return (
    <>
      <Uploader
        isSmart
        onStoreFile={setFile}
        processFile={async ({ organizationId, file }) => {
          setIsProcessing(true)
          return api.beneficiaries.one.processProfile({
            organizationId,
            file,
          })
        }}
        onUploaded={(response) => {
          setIsProcessing(false)
          if (hasError(response)) return
          setValues({
            fullName: response.fullName,
            profile: { value: response.profile },
          })
        }}
      />
      <BeneficiaryForm disabled={isProcessing} values={values} onCreate={handleSubmit} />
    </>
  )
}

function BeneficiaryForm({
  onCreate,
  values,
  disabled,
}: {
  onCreate: (beneficiaryId: string) => void
  values?: {
    fullName: string
    profile: {
      value: string
    }
  }
  disabled?: boolean
}) {
  const { organizationId } = useParams<AvailableParams>()
  if (!organizationId) return null

  const dispatch = useStoreDispatch()
  const view = useView()
  const { tc } = useT()

  const {
    data: [organization],
  } = useAsyncData([getCurrentOrganization])

  const defaultValues = useMemo(
    () => ({
      fullName: '',
      profile: {
        value: organization?.beneficiaryProfileTemplate || '',
      },
    }),
    [organization?.beneficiaryProfileTemplate],
  )

  const { form, isFormValid, submit, submitOnPressMetaEnter } = useFormValidator({
    defaultValues,
    onSubmit: async (values) => {
      const { beneficiary } = await dispatch(
        storeActions.beneficiary.create({
          beneficiaryFields: values,
        }),
      ).unwrap()

      view.beneficiary(beneficiary.id).profile.navigate()
      form.reset(defaultValues)

      onCreate(beneficiary.id)
    },
    schema: beneficiarySchema,
  })

  useEffect(() => {
    if (values) form.reset(values)
  }, [values])
  return (
    <>
      <Form {...form}>
        <form onSubmit={submit} className="space-y-2" onKeyDown={submitOnPressMetaEnter}>
          <FormField
            control={form.control}
            name="fullName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{tc('fullName')}</FormLabel>
                <FormControl>
                  <Input disabled={disabled} placeholder={tc('fullName')} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="profile.value"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{tc('profile', { cfl: true })}</FormLabel>
                <FormControl>
                  <Textarea
                    disabled={disabled}
                    placeholder={tc('profile', { cfl: true })}
                    {...field}
                    className="h-60 text-base"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <DialogFooter>
            <Button type="submit" disabled={!isFormValid || disabled}>
              {tc('create', { cfl: true })}
            </Button>
          </DialogFooter>
        </form>
      </Form>
    </>
  )
}
